import React, { useMemo, useState } from "react";
import EventExperienceCard from "./EventExperienceCard";
import EventAudienceCard, { EventAudienceCardData } from "./EventAudienceCard";
import { container_classname } from "./constants";

type TabId = "experience" | "audience";

interface EventExperienceItem {
    eyebrow: string;
    title: string;
    description: string;
    image: string;
    mediaPosition: "left" | "right";
    icon: string;
}

export interface EventTabbedHighlightsData {
    tabs: {
        experience: string;
        audience: string;
    };
    experienceItems: EventExperienceItem[];
    audienceItems: EventAudienceCardData[];
}

interface EventTabbedHighlightsSectionProps {
    data: EventTabbedHighlightsData;
}

const tabIds: TabId[] = ["experience", "audience"];

const EventTabbedHighlightsSection: React.FC<EventTabbedHighlightsSectionProps> = ({ data }) => {
    const [activeTab, setActiveTab] = useState<TabId>("experience");

    const experienceCards = useMemo(() => data.experienceItems, [data.experienceItems]);

    return (
        <section className="bg-white py-14 lg:py-20">
            <div className={container_classname}>
                <div className="mx-auto max-w-6xl">
                    <div className="flex justify-center">
                        <div
                            className="inline-flex rounded-2xl border border-gray-200 bg-white p-3"
                            style={{ border: "1px solid rgba(226, 232, 240, 1)", backgroundColor: "rgba(247, 250, 252, 1)" }}
                            role="tablist"
                            aria-label="Event highlights"
                        >
                            {tabIds.map((tabId) => {
                                const isActive = activeTab === tabId;

                                return (
                                    <button
                                        key={tabId}
                                        type="button"
                                        className={`rounded-md px-4 py-3 text-sm font-semibold transition-colors font-inter ${isActive ? "text-white" : "text-gray-900"}`}
                                        style={{
                                            backgroundColor: isActive ? "#3364FA" : "transparent",
                                            boxShadow: isActive ? "0px 8px 12px 0px rgba(3, 4, 4, 0.36)" : "none",
                                        }}
                                        onClick={() => setActiveTab(tabId)}
                                        role="tab"
                                        aria-selected={isActive}
                                    >
                                        {data.tabs[tabId]}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="mt-12">
                        {activeTab === "experience" ? (
                            <div>
                                {experienceCards.map((item, index) => (
                                    <EventExperienceCard key={`${item.title}-${item.eyebrow}`} data={item} lastItem={experienceCards.length - 1 === index} />
                                ))}
                            </div>
                        ) : (
                            <div>
                                {data.audienceItems.map((item, index) => (
                                    <EventAudienceCard key={`${item.title}-${item.description}`} data={item} lastItem={data.audienceItems.length - 1 === index} />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default EventTabbedHighlightsSection;
